/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {currentTimeMillies, generateHex, Module} from "@intuitionrobotics/ts-common";
import {XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {ApiPublishEvents, BaseEvent} from "@app/app-shared/api";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";

const throttleTime = 5000
const batchSize = 100
const SectionIdVar = 'section_id';
const SessionInactivityTimeout = 300000;

export const AnalyticConsts = {
    ActivityStatus: {
        CLICKED: "clicked",
        CREATE: "create",
        UPDATE: "update",
        COMPLETE: "complete",
        VIEW: "view",
        DELETE: "delete",
        SCHEDULE: "schedule"
    },
    Entities: {
        USER: "user",
        SYSTEM: "system"
    },
    ObjectTypes: {
        MESSAGES: "messages",
        LOGIN: "login",
        CUSTOMERS: "customers",
        HEDIS: "hedis",
    },
    ObjectSubTypes: {
        CHAT_BOX: "chat_box",
        CARE_PLAN: "care_plan",
        DASHBOARD: "dashboard",
        COMPOSE_MESSAGE: "compose_message",
        WELLNESS_PROGRAM: "wellness_program",
        MEASUREMENT: "measurement",
        SCREENING_REQUEST: "screening_request"
    },
    ActivityDetails: {
        SEND_MESSAGE: "send_message",
        NOTIFICATION_CHANGE: "notification_change",
        MEDICATION_REMINDER: "medication_reminder"
    },
    Frequency: {
        SCHEDULED: "scheduled"
    }
};


class AnalyticsModule_Class
    extends Module {

    readonly timeZone: string
    private flushInterval: NodeJS.Timeout | undefined
    private section_id: string = ""
    private eventQueue: BaseEvent[]
    private sectionTimeoutId: NodeJS.Timeout | undefined;
    private userId?: string

    constructor() {
        super("AnalyticsModule");
        this.loadOrCreateSectionId();
        this.addBeforeUnloadListener();

        this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.eventQueue = this.getOrSetEventQueue();
    }

    public setUserId(userId: string) {
        this.userId = userId
    }


    public logCareManagerEvent({
        objectType,
        entity,
        activityStatus,
        objectSubType,
        activityDetails,
        frequency,
        frequencyValue,
        objectId,
        creator = "care_center_user"
    }: {
        objectType: string,
        entity: string,
        activityStatus: string,
        objectSubType?: string,
        activityDetails?: string,
        frequency?: string,
        frequencyValue?: number,
        objectId?: string,
        creator?: string
    }) {
        this.addEventToQueue({
                factType: "CARE_ACTION",
                activityStatus,
                activityDetails,
                entity,
                objectType,
                objectSubType,
                sectionId: this.section_id,
                timeStamp: currentTimeMillies(),
                timeZone: this.timeZone,
                frequency,
                frequencyValue,
                userId: this.userId || "",
                id: generateHex(16),
                objectId,
                creator,
            }
        )
    }

    private addEventToQueue<T extends BaseEvent>(body: T) {
        this.eventQueue.push(body);
        this.saveEventQueueInsSessionStorage();

        if (this.flushInterval)
            clearTimeout(this.flushInterval);

        if (this.eventQueue.length >= batchSize)
            this.sendBatch();
        else {
            this.flushInterval = setTimeout(() => {
                this.flushInterval = undefined;
                this.sendBatch();
            }, throttleTime);
        }

        this.resetSectionTimer();
    }

    private sendBatch(): void {
        if (!this.userId) {
            this.flushInterval = setTimeout(() => this.sendBatch(), throttleTime);
            return;
        }

        const eventQueue = this.eventQueue.map(e => ({
            ...e,
            userId: this.userId,
            timeZone: this.timeZone
        }))

        if (eventQueue.length === 0)
            return;

        this.sendActivityEvents(eventQueue)
            .then(() => {
                this.logInfo("Batch sent successfully. Cleaning the eventQueue");
                this.eventQueue = this.eventQueue.filter(e => !eventQueue.find(ev => ev.id === e.id));
                this.saveEventQueueInsSessionStorage();
            })
            .catch((error) => {
                this.logError("Failed to send batch events", error);
                this.flushInterval = setTimeout(() => this.sendBatch(), throttleTime);
            });
    }

    private sendActivityEvents(body: BaseEvent[]) {
        return XhrHttpModule
            .createRequest<ApiPublishEvents>(HttpMethod.POST, "user-events")
            .setRelativeUrl("/v1/user/user-events")
            .setJsonBody(body)
            .executeSync();
    }


    private loadOrCreateSectionId(): void {
        this.section_id = sessionStorage.getItem(SectionIdVar) || this.resetSectionId();
    }

    private resetSectionId(): string {
        const section_id = generateHex(16);
        sessionStorage.setItem(SectionIdVar, section_id);
        this.logInfo(`New section ID generated: ${section_id}`);
        return section_id;
    }

    private resetSectionTimer() {
        // Clear any existing timeout
        if (this.sectionTimeoutId)
            clearTimeout(this.sectionTimeoutId);

        // Set a new timeout for 5 minutes (300000 ms)
        this.sectionTimeoutId = setTimeout(() => {
            this.section_id = this.resetSectionId();
        }, SessionInactivityTimeout);
    }

    private addBeforeUnloadListener(): void {
        window.addEventListener('beforeunload', () => this.sendBatch());
    }

    private getOrSetEventQueue(): BaseEvent[] {
        try {
            const storedEventQueue = sessionStorage.getItem('eventQueue');
            if (storedEventQueue)
                return JSON.parse(storedEventQueue);
        } catch (error) {
            console.error('Error parsing eventQueue from sessionStorage:', error);
        }
        // If parsing fails or no stored data, return an empty array
        return [];
    }


    private saveEventQueueInsSessionStorage(): void {
        sessionStorage.setItem('eventQueue', JSON.stringify(this.eventQueue));
    }
}

export const AnalyticsModule = new AnalyticsModule_Class();
